import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Col, Row, Typography, Table, Button, DatePicker, Tooltip } from 'antd';
import { ExportOutlined, EyeOutlined } from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import { fetchSessionsList, clearSessions, exportSessionsByFilter } from 'state/actions/session';
import './SessionAnalyticDashboard.css';

const { Text } = Typography;

function SessionAnalyticsDashboard() {
  const history = useHistory();
  const dispatch = useDispatch();
  const { siteId } = useParams();

  const { pagedList, loading, exporting } = useSelector(state => ({
    pagedList: state.sessionAnalytic.pagedList,
    loading: state.sessionAnalytic.loading,
    exporting: state.sessionAnalytic.exporting || false,
  }));

  const [dateRange, setDateRange] = useState(null);
  const [tableParams, setTableParams] = useState({
      pagination: {
          current: 1,
          pageSize: 10,
      },
  });

  const fetchData = useCallback(() => {
    const params = {
        page: tableParams.pagination.current,
        limit: tableParams.pagination.pageSize,
        siteId,
        sortBy: tableParams.sorter?.field,
        sortOrder: tableParams.sorter?.order === 'ascend' ? 'asc' : 'desc',
    };
    if (dateRange && dateRange.length === 2) {
        params.startTime = dateRange[0].startOf('day').toISOString();
        params.endTime = dateRange[1].endOf('day').toISOString();
    }
    dispatch(fetchSessionsList(params));
  }, [dispatch, dateRange, tableParams, siteId]);

  useEffect(() => {
    fetchData();
    return () => {
        dispatch(clearSessions());
    };
  }, [fetchData, dispatch]);

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
        pagination,
        filters,
        sorter: Array.isArray(sorter) ? sorter[0] : sorter,
    });
  };
  
  const handleShowDetails = (sessionId) => {
      history.push(`/session-analytics/${sessionId}`);
  };

  const handleExportCSV = () => {
    const filter = { siteId };
    if (dateRange && dateRange.length === 2) {
      filter.start = dateRange[0].startOf('day').toISOString();
      filter.end = dateRange[1].endOf('day').toISOString();
    }
    dispatch(exportSessionsByFilter(filter));
  };


  const columns = [
    {
      title: 'SESSION',
      dataIndex: 'sessionId',
      key: 'sessionId',
      render: (id) => <Text style={{ fontFamily: 'monospace' }}>{id ? id.substring(0, 8) : 'N/A'}</Text>,
      width: 120,
    },
    {
      title: 'SITE',
      dataIndex: 'siteName',
      key: 'siteName',
      render: (device) => <Text>{device || 'N/A'}</Text>,
      width: 200,
    },
    {
      title: 'START DATE',
      dataIndex: 'sessionStart',
      key: 'sessionStart',
      render: (date) => (date ? moment(date).format('MMM DD, YYYY, hh:mm:ss A') : 'N/A'),
      sorter: true,
      width: 220,
    },
    {
      title: 'END DATE',
      dataIndex: 'sessionEnd',
      key: 'sessionEnd',
      render: (date) => (date ? moment(date).format('MMM DD, YYYY, hh:mm:ss A') : 'N/A'),
      sorter: true,
      width: 220,
    },
    {
      title: 'SUMMARY',
      dataIndex: 'sessionSummary',
      key: 'sessionSummary',
      ellipsis: { showTitle: false },
      render: (summary) => <Tooltip placement="topLeft" title={summary}>{summary || 'No summary available.'}</Tooltip>,
    },
    {
      title: 'ACTION',
      key: 'action',
      width: 150,
      align: 'center',
      render: (_, record) => <Button type="link" icon={<EyeOutlined />} onClick={() => handleShowDetails(record.sessionId)}>See Details</Button>,
    },
  ];

  return (
    <div className="analytic">
      <Card style={{ marginBottom: '24px', borderRadius: '8px' }}>
        <Row gutter={[16, 16]} justify="space-between" align="middle">
          <Col xs={24} sm={12} md={14}>
            <DatePicker.RangePicker
              className="analytic-date-picker"
              onChange={(dates) => setDateRange(dates)}
            />
          </Col>
          <Col xs={24} sm={12} md={4}>
            <Button
              className="btn-outline-primary"
              icon={<ExportOutlined />}
              onClick={handleExportCSV}
              disabled={exporting} 
            >
              {exporting ? 'Exporting...' : 'Export CSV'}
            </Button>
          </Col>
        </Row>
      </Card>
      <Card style={{ borderRadius: '8px' }}>
        <Table
          columns={columns}
          dataSource={pagedList.docs}
          loading={loading}
          rowKey="sessionId"
          pagination={{
            current: pagedList.page,
            pageSize: pagedList.limit,
            total: pagedList.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
          }}
          onChange={handleTableChange}
          scroll={{ x: 1200 }}
          size="middle"
        />
      </Card>
    </div>
  );
}

export default SessionAnalyticsDashboard;